// Stage 3 — Quality Check (FR-3.*, July-2 FR-J13–J16, July-7 FR-K12–K16). The queue is grouped
// by preprint under a COLLAPSIBLE title header (FR-K13); each reviewer card carries the full
// detail set — email, PubMed, nearest neighbor, institutional profile, justification, proposing
// student name + email (FR-K12). Fixable/Reject prompt for STUDENT-facing feedback and Double
// Check for a SENIOR-AE-facing internal note (FR-K14). Students without QC access get "My
// Quality Checks" — their proposals + AE feedback with Edit, Reply, and Fixable → Fixed (FR-K16).
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  REVIEWER_STATUSES,
  RRID_COUNTRY_TEAMS,
  nextStates,
  reviewerAttentionRank,
  type Action,
  type Domain,
  type PreprintStatus,
  type ProposalView,
  type ReviewerStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { pubmedSearchUrl } from "../../lib/pubmed.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { useProductEvent } from "../../lib/telemetry.js";
import { Badge, Button, Card, CardBody, EmptyState, Field, Input, Modal, Select, Spinner, Textarea, statusTone } from "../../components/ui.js";
import { MultiFilter } from "../../components/MultiFilter.js";
import { RichText } from "../../components/RichText.js";
import { ReviewerProfilePanel } from "../preprints/ReviewerProfilePanel.js";
import { ClickableUrlField } from "./ClickableUrlField.js";
import { HighestDegreeField } from "../reviewers/HighestDegreeField.js";

/** QC rows arrive enriched with the preprint title + selectedId for grouping (FR-J15/J16) and
 *  the pipeline status backing the in-process/complete queue split (Frida-QC R-003/R-004).
 *  preprintAbstract backs the student My-QC expand-in-place disclosure. */
type QcRow = ProposalView & {
  preprintTitle: string;
  selectedId: string | null;
  preprintStatus: PreprintStatus | null;
  preprintAbstract: string | null;
  preprintCreatedAt: string | null;
};

/** Stick the QC title + filters just below the app nav so the heading is never clipped. */
const QC_STICKY_BAR =
  "sticky top-[var(--app-header-h,4rem)] z-20 -mx-4 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 bg-white px-4 pb-3 pt-2 shadow-sm";

type QcSort =
  | "proposals-newest"
  | "proposals-oldest"
  | "preprints-newest"
  | "preprints-oldest";

const QC_SORT_OPTIONS: { value: QcSort; label: string }[] = [
  { value: "proposals-newest", label: "Most to least recent proposals" },
  { value: "proposals-oldest", label: "Least to most recent proposals" },
  { value: "preprints-newest", label: "Most to least recent preprints" },
  { value: "preprints-oldest", label: "Least to most recent preprints" },
];

const parseTime = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

function groupSortTime(
  rows: QcRow[],
  kind: "proposals" | "preprints",
): number {
  if (kind === "proposals") {
    let best: number | null = null;
    for (const r of rows) {
      const t = parseTime(r.dateOfProposal);
      if (t == null) continue;
      best = best == null ? t : Math.max(best, t);
    }
    return best ?? 0;
  }
  for (const r of rows) {
    const t = parseTime(r.preprintCreatedAt);
    if (t != null) return t;
  }
  let earliest: number | null = null;
  for (const r of rows) {
    const t = parseTime(r.dateOfProposal);
    if (t == null) continue;
    earliest = earliest == null ? t : Math.min(earliest, t);
  }
  return earliest ?? 0;
}

const PITCH_CLAIM_PAIRS = [1, 2, 3].map((n) => ({
  rq: `Research Q${n}`,
  claim: `Main Claim ${n}`,
  rqLabel: `Research Question ${n}`,
  claimLabel: `Main Claim ${n}`,
}));

/** Compact read-only pitch claims — lazy-loaded when the Pitch deck disclosure opens. */
function PitchDeckPreview({ selectedId }: { selectedId: string }): JSX.Element {
  const { data: s, isLoading } = useQuery({
    queryKey: qk.selectedOne(selectedId),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${selectedId}`),
  });
  if (isLoading) return <Spinner label="Loading pitch deck…" />;
  if (!s) return <p className="text-xs text-slate-500">Pitch deck unavailable.</p>;
  const pitch = s.pitch as Record<string, unknown>;
  const val = (key: string): string => String(pitch[key] ?? "").trim();
  const pairs = PITCH_CLAIM_PAIRS.filter((c) => val(c.rq) || val(c.claim));
  const noveltyNote = val("Priority(novelty) Note");
  const limitations = val("Potential Limitation");
  if (pairs.length === 0 && !noveltyNote && !limitations) {
    return (
      <p className="text-xs text-slate-500">
        No pitch claims on file.{" "}
        <Link
          className="text-brand hover:underline"
          to={`/preprints/${selectedId}`}
          target="_blank"
          rel="noreferrer"
        >
          Open full deck ↗
        </Link>
      </p>
    );
  }
  return (
    <div className="space-y-2.5">
      {pairs.map((c) => (
        <div key={c.rq} className="space-y-1">
          {val(c.rq) ? (
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{c.rqLabel}</span>
              <RichText className="text-xs leading-relaxed text-slate-600">{val(c.rq)}</RichText>
            </div>
          ) : null}
          {val(c.claim) ? (
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{c.claimLabel}</span>
              <RichText className="text-xs leading-relaxed text-slate-600">{val(c.claim)}</RichText>
            </div>
          ) : null}
        </div>
      ))}
      {limitations ? (
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Potential Limitations</span>
          <RichText className="text-xs leading-relaxed text-slate-600">{limitations}</RichText>
        </div>
      ) : null}
      {noveltyNote ? (
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Novelty</span>
          <RichText className="text-xs leading-relaxed text-slate-600">{noveltyNote}</RichText>
        </div>
      ) : null}
      <Link
        className="inline-block text-xs font-medium text-brand hover:underline"
        to={`/preprints/${selectedId}`}
        target="_blank"
        rel="noreferrer"
      >
        Open full pitch deck ↗
      </Link>
    </div>
  );
}

function ChevronDown({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={`transition-transform ${open ? "rotate-180" : ""}`}
    >
      <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Linked preprint title + Abstract / Pitch deck / proposed-reviewers disclosures. */
function PreprintContext({
  selectedId,
  title,
  abstract,
  trailing,
  compact = false,
  proposedCount,
  proposedDefaultOpen = false,
  proposedReviewers,
}: {
  selectedId: string | null | undefined;
  title: string;
  abstract: string | null | undefined;
  trailing?: ReactNode;
  /** Smaller title styling when shown under a reviewer name on Other-feedback cards. */
  compact?: boolean;
  /** Label count for the proposed-reviewers disclosure (`2 proposed reviewers`). */
  proposedCount?: number;
  /** Groups that need AE re-check / student fixes start expanded (FR-K13 / FR-L7). */
  proposedDefaultOpen?: boolean;
  proposedReviewers?: ReactNode;
}): JSX.Element {
  const [pitchOpen, setPitchOpen] = useState(false);
  const [proposedOpen, setProposedOpen] = useState(proposedDefaultOpen);
  useEffect(() => {
    if (proposedDefaultOpen) setProposedOpen(true);
  }, [proposedDefaultOpen]);
  const heading = selectedId ? (
    <Link
      to={`/preprints/${selectedId}`}
      className="text-brand hover:underline"
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {title}
    </Link>
  ) : (
    <span>{title}</span>
  );
  const body = abstract?.trim();
  const proposedLabel =
    proposedCount == null
      ? null
      : `${proposedCount} proposed reviewer${proposedCount === 1 ? "" : "s"}`;
  const toggleProposed = (): void => setProposedOpen((v) => !v);
  return (
    <div className="relative min-w-0">
      {proposedLabel != null && proposedReviewers != null ? (
        <button
          type="button"
          className="absolute right-0 top-0 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          aria-expanded={proposedOpen}
          aria-label={proposedOpen ? "Hide proposed reviewers" : "Show proposed reviewers"}
          onClick={toggleProposed}
        >
          <ChevronDown open={proposedOpen} />
        </button>
      ) : null}
      <div className={compact ? "text-xs text-slate-500" : "pr-8 text-sm font-semibold text-slate-700"}>
        {!compact && "📄 "}
        {heading}
        {compact ? trailing : null}
      </div>
      {!compact && trailing ? <div className="mt-0.5 pr-8">{trailing}</div> : null}
      {!compact ? (
        <div className="mt-1 flex flex-col gap-1 pr-8" onClick={(e) => e.stopPropagation()}>
          {body ? (
            <details className="max-w-3xl">
              <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
                Abstract
              </summary>
              <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{body}</p>
            </details>
          ) : null}
          {selectedId ? (
            <details
              className="max-w-3xl"
              onToggle={(e) => setPitchOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
                Pitch deck
              </summary>
              <div className="mt-1.5 rounded-md border border-slate-100 bg-slate-50/70 p-2.5">
                {pitchOpen ? <PitchDeckPreview selectedId={selectedId} /> : null}
              </div>
            </details>
          ) : null}
          {proposedLabel != null && proposedReviewers != null ? (
            <div className="w-full">
              <button
                type="button"
                className="flex cursor-pointer items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                aria-expanded={proposedOpen}
                onClick={toggleProposed}
              >
                <ChevronDown open={proposedOpen} />
                {proposedLabel}
              </button>
              {proposedOpen ? <div className="mt-1.5">{proposedReviewers}</div> : null}
            </div>
          ) : null}
        </div>
      ) : body ? (
        <details className="mt-1" onClick={(e) => e.stopPropagation()}>
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
            Abstract
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{body}</p>
        </details>
      ) : null}
    </div>
  );
}

// Trimmed dropdown (FR-J13): Tentative Invite deduped into Double Check; H-index gates,
// Incomplete, and the old Invite Backup are retired.
const STATUS_ACTIONS: Partial<Record<ReviewerStatus, Action>> = {
  Invite: "qcSetInvite",
  "Invite Backup Unchecked": "qcSetInviteBackup",
  "Double Check": "qcSetDoubleCheck",
  Reject: "qcSetReject",
  Fixable: "qcSetFixable",
  Unchecked: "qcSetUnchecked",
  Fixed: "markReviewerFixed",
  "Student Reject": "studentRejectCandidate",
};
const SETTABLE_STATUSES = Object.keys(STATUS_ACTIONS) as ReviewerStatus[];

// Comment popups per status (FR-K14): who reads the comment decides where it's stored.
// Frida-QC R-012: the prompts coach a positive + corrective tone, and Invite takes an OPTIONAL
// celebratory note (Fixable/Reject/Double Check stay required).
const COMMENT_PROMPTS: Partial<Record<ReviewerStatus, string>> = {
  Fixable: "Feedback for the proposing student (required for Fixable) — start with what they got right, then the fix:",
  Reject: "Feedback for the proposing student (required for Reject) — note what was good before the reason:",
  "Double Check": "Internal note for the senior AE (required for Double Check):",
  Invite: "Optional note for the proposing student — what made this reviewer a great pick? (Leave empty to skip.)",
};
const OPTIONAL_COMMENT_STATUSES = new Set<ReviewerStatus>(["Invite"]);

/** Older imported comments carry literal `\n` sequences — render them as real line breaks. */
const unescapeNewlines = (s: string): string => s.replace(/\\n/g, "\n");

/** Best-match link target: a full URL as-is; a bare DOI resolves via doi.org. */
const bestMatchHref = (v: string): string => (/^https?:\/\//i.test(v) ? v : `https://doi.org/${v}`);

/** Student-filled URL as stored — only add a scheme so the click is not a same-origin path. */
function extHref(raw: string | null | undefined): string | null {
  const v = raw?.trim();
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

function ExtLink({
  href,
  children,
  title,
}: {
  href: string;
  children: ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <a className="text-brand hover:underline" href={href} title={title} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function qcCheckedByLine(p: Pick<QcRow, "qcEmail" | "qcName" | "dateOfQc">): string | null {
  if (!p.qcEmail && !p.dateOfQc) return null;
  const name = p.qcName?.trim() || p.qcEmail?.split("@")[0] || "—";
  const email = p.qcEmail;
  const date = p.dateOfQc?.slice(0, 10);
  let line = name;
  if (email) line += ` (${email})`;
  if (date) line += ` on ${date}`;
  return line;
}

const PUBMED_TIP = "Tip: search the reviewer's FULL name on PubMed — results open sorted by most recent.";

export function QualityCheckPortal(): JSX.Element {
  useProductEvent("ui.qc_opened", { path: "/quality-check" });
  const can = useCan();
  // July-8 FR-L9: the QC queue is cross-domain for QC-capable users — no domain in the gate,
  // and "All domains" is the default view (domain is an optional filter, not a restriction).
  const [domain, setDomain] = useState<Domain | "">("");
  const allowed = can("qcOpenPortal", {});
  // Students (no QC-portal capability) get their own scoped view instead (FR-J14/L4).
  return allowed ? <QcQueue domain={domain} setDomain={setDomain} /> : <MyQualityChecks />;
}

// ── Full QC queue (AE / QC roles) ─────────────────────────────────────────────
// SoftR R-008 + Frida-QC R-005: the queue opens on the AE's real worklist — fresh Unchecked
// rows, Fixed rows awaiting re-check, and Double-check rows awaiting a senior — as one combined
// default selection.
const DEFAULT_STATUSES = "Unchecked,Fixed,Double Check";
type QueueKind = "in-process" | "complete" | "all";

/** Pipeline statuses whose reviewer selection is DONE — mirrors the server's split so the student
 *  page (which filters client-side over /quality-check/mine) lands in the same buckets. */
const COMPLETE_QUEUE_STATUSES = new Set<string>(["INVITED", "COMPLETE", "DISCARDED"]);

/** The in-process / complete / all tab strip, shared by the AE queue and the student page. */
function QueueTabs({ queue, onChange }: { queue: QueueKind; onChange: (q: QueueKind) => void }): JSX.Element {
  return (
    <div className="flex overflow-hidden rounded-md border border-slate-200" role="group" aria-label="Queue">
      {(
        [
          ["in-process", "In process"],
          ["complete", "Complete"],
          ["all", "All"],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={queue === value}
          className={`px-2.5 py-1.5 text-sm ${queue === value ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          onClick={() => onChange(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function QcQueue({ domain, setDomain }: { domain: Domain | ""; setDomain: (d: Domain | "") => void }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const me = user?.email.toLowerCase() ?? "";
  const [status, setStatus] = useState<ReviewerStatus | typeof DEFAULT_STATUSES | "">(DEFAULT_STATUSES);
  // Frida-QC R-002 / WO-7/21 Track I: multi-select team filter + the in-process/complete queue
  // split (in-process default). Teams are the geographic unit — there is no separate country
  // field in the data, so the team filter IS the country filter.
  const [teamsSel, setTeamsSel] = useState<string[]>([]);
  const [queue, setQueue] = useState<QueueKind>("in-process");
  // WO-7/22 Track G: "Claimed by me" [35:57], student name/email [38:00–39:44], and a
  // recent/older date-of-proposal sort — all client-side over the fetched queue.
  const [claimedOnly, setClaimedOnly] = useState(false);
  const [studentFilter, setStudentFilter] = useState("");
  // Angel (Stef mtg 8/20): oldest proposals first by default — work the backlog top-down.
  const [dateOrder, setDateOrder] = useState<QcSort>("proposals-oldest");
  // WO-7/22 Track G [43:11–44:44] + Track J: status + comment land in ONE modal action (replaces
  // the window.prompt popup that rendered broken in Chrome).
  const [statusPick, setStatusPick] = useState<{ row: QcRow; status: ReviewerStatus } | null>(null);
  // Frida-QC R-007: senior profile view/edit from the queue (server-gated by qcConfirmDoubleCheck).
  const [editing, setEditing] = useState<QcRow | null>(null);

  const params = new URLSearchParams();
  if (domain) params.set("domain", domain);
  if (teamsSel.length) params.set("teams", teamsSel.join(","));
  if (queue !== "all") params.set("queue", queue);
  if (status === DEFAULT_STATUSES) params.set("statuses", DEFAULT_STATUSES);
  else if (status) params.set("status", status);
  // FR-L9: QC list + status mutations are cross-domain. Do not pass the filter domain into
  // can() — that made "All domains" look allowed while a MedSci AE on a BCS row got POST 403
  // (#32), and made filtering to another domain hide Invite even when the server allows it.
  const qcAuth = {};
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ domain, teams: teamsSel.join(","), queue, status }),
    queryFn: () => api.get<QcRow[]>(`/quality-check?${params.toString()}`),
  });

  const setStatusM = useMutation({
    mutationFn: ({ id, status, comments }: { id: string; status: ReviewerStatus; comments?: string }) =>
      api.post(`/quality-check/${id}/status`, { status, comments }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qc"] });
      setEditing(null);
    },
  });
  // Frida-QC R-008: persisted claim — every AE sees who is working the row.
  const claimM = useMutation({
    mutationFn: ({ id, claim }: { id: string; claim: boolean }) =>
      api.post(claim ? `/quality-check/${id}/claim` : `/quality-check/${id}/unclaim`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });

  // WO-7/22 Track G: the client-side filter set applied before grouping — claimed-only and
  // student (name AND email, global across preprints). Date order is applied when grouping.
  const filtered = useMemo(() => {
    let rows = data ?? [];
    if (claimedOnly) {
      rows = rows.filter((p) => (p.qcClaimedByEmail ?? "").toLowerCase() === me);
    }
    const q = studentFilter.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (p) =>
          (p.studentName ?? "").toLowerCase().includes(q) ||
          (p.proposerEmail ?? "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, claimedOnly, me, studentFilter]);

  // Group by preprint, title prominent (FR-J15); proposed reviewers collapse behind a
  // count-labelled disclosure like Abstract / Pitch deck (FR-K13). Within a group:
  // claimed rows first (Frida-QC R-008), then Fixed (a fix awaiting re-check) →
  // Double Check (awaiting a senior) → Unchecked → the rest.
  const groups = useMemo(() => {
    const statusRank = (s: ReviewerStatus): number =>
      s === "Fixed" ? 0 : s === "Double Check" ? 1 : s === "Unchecked" ? 2 : 3;
    const rowRank = (p: QcRow): number => (p.qcClaimedByEmail ? -1 : statusRank(p.status));
    const byPreprint = new Map<
      string,
      { title: string; selectedId: string | null; abstract: string | null; rows: QcRow[] }
    >();
    for (const p of filtered) {
      const key = p.preprintId ?? "unknown";
      const g = byPreprint.get(key) ?? {
        title: p.preprintTitle,
        selectedId: p.selectedId,
        abstract: p.preprintAbstract,
        rows: [],
      };
      if (!g.selectedId && p.selectedId) g.selectedId = p.selectedId;
      if (!g.abstract && p.preprintAbstract) g.abstract = p.preprintAbstract;
      g.rows.push(p);
      byPreprint.set(key, g);
    }
    for (const g of byPreprint.values()) {
      g.rows.sort((a, b) => rowRank(a) - rowRank(b));
    }
    const groupTime = (g: { rows: QcRow[] }): number =>
      groupSortTime(g.rows, dateOrder.startsWith("preprints") ? "preprints" : "proposals");
    const newestFirst = dateOrder.endsWith("newest");
    return [...byPreprint.entries()].toSorted((a, b) =>
      newestFirst ? groupTime(b[1]) - groupTime(a[1]) : groupTime(a[1]) - groupTime(b[1]),
    );
  }, [filtered, dateOrder]);

  return (
    <div className="space-y-4">
      <header className={QC_STICKY_BAR}>
        <div>
          <h1 className="text-xl font-bold">Quality Check</h1>
          <p className="text-sm text-slate-500">Grouped by preprint — review each proposed reviewer and set a status.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Frida-QC R-003/R-004: in-process (default) vs complete queues — complete stays
              reachable after the 40-greenlit cutoff for outstanding student QC + teaching. */}
          <QueueTabs queue={queue} onChange={setQueue} />
          {/* FR-L9: domain is an optional filter — the queue itself is cross-domain. */}
          <Select value={domain} onChange={(e) => setDomain(e.target.value as Domain | "")}>
            <option value="">All domains</option>
            {DOMAINS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
          {/* Frida-QC R-002 / WO-7/21 Track I: country/team multi-select (fixed RRID vocabulary). */}
          <MultiFilter label="Country / Team" options={RRID_COUNTRY_TEAMS} value={teamsSel} onChange={setTeamsSel} />
          <Select value={status} onChange={(e) => setStatus(e.target.value as ReviewerStatus | typeof DEFAULT_STATUSES | "")}>
            <option value={DEFAULT_STATUSES}>Unchecked + Fixed + Double check</option>
            <option value="">All statuses</option>
            {REVIEWER_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
          {/* WO-7/22 Track G: claimed-by-me / student / date-of-proposal filters. */}
          <Button
            variant={claimedOnly ? "primary" : "secondary"}
            className="py-1.5"
            aria-pressed={claimedOnly}
            onClick={() => setClaimedOnly((v) => !v)}
          >
            📌 Claimed by me
          </Button>
          <Input
            className="w-44"
            placeholder="Student name or email"
            aria-label="Filter by proposing student (name or email)"
            value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)}
          />
          <Select
            value={dateOrder}
            aria-label="Sort by recency"
            onChange={(e) => setDateOrder(e.target.value as QcSort)}
          >
            {QC_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </header>

      {isLoading ? (
        <Spinner label="Loading queue…" />
      ) : error ? (
        <EmptyState>Could not load the QC queue.</EmptyState>
      ) : groups.length === 0 ? (
        <EmptyState>No reviewers match this filter.</EmptyState>
      ) : (
        <div className="space-y-4">
          {groups.map(([preprintId, g]) => {
            // Expand only groups with Fixed (student fixed — AE needs to re-check); rest stay
            // collapsed (FR-K13 / FR-L7) so the AE scans titles and opens what they need.
            return (
            <div
              key={preprintId}
              className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
            >
              <PreprintContext
                selectedId={g.selectedId}
                title={g.title}
                abstract={g.abstract}
                proposedCount={g.rows.length}
                proposedDefaultOpen={editing?.preprintId === preprintId}
                proposedReviewers={
                  /* WO-7/22 Track G [33:36–35:48]: side-by-side — the reviewer profile stays
                      CONSTANTLY visible beside the proposals, no modal to lose. */
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
                    <div className="space-y-2">
                      {g.rows.map((p) => {
                        const mine = (p.qcClaimedByEmail ?? "").toLowerCase() === me;
                        const claimed = !!p.qcClaimedByEmail;
                        return (
                        <QcProposalRow
                          key={p.id}
                          p={p}
                          canConfirmDouble={can("qcConfirmDoubleCheck", qcAuth)}
                          canForStatus={(t) =>
                            can(STATUS_ACTIONS[t]!, qcAuth) || (t === "Invite" && can("qcJuniorInvite", qcAuth))
                          }
                          claimed={claimed}
                          mine={mine}
                          onToggleClaim={() => claimM.mutate({ id: p.id, claim: !mine })}
                          canEditProfile={can("qcConfirmDoubleCheck", qcAuth)}
                          onQualityCheck={() => setEditing(p)}
                          claimPending={claimM.isPending}
                        />
                        );
                      })}
                    </div>
                    {g.selectedId && (
                      <aside className="h-fit rounded-md border border-slate-100 bg-slate-50/60 p-3 lg:sticky lg:top-16">
                        <h4 className="mb-2 text-xs font-semibold text-slate-600">
                          Reviewer profile — who we're looking for
                        </h4>
                        <ReviewerProfilePanel
                          selectedId={g.selectedId}
                          canEdit={can("editSimilarArticles", qcAuth) || can("editPitchDeck", qcAuth)}
                        />
                      </aside>
                    )}
                  </div>
                }
              />
            </div>
            );
          })}
        </div>
      )}

      {/* Quality Check + reviewer profile sit side-by-side in a centered panel so both stay
          readable without pinning one to each edge of the screen. */}
      {editing && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20 p-4"
          onClick={() => {
            setStatusPick(null);
            setEditing(null);
          }}
        >
          <div
            className="flex max-h-[min(46rem,calc(100vh-5rem))] w-full max-w-5xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {statusPick ? (
              <StatusCommentModal
                row={statusPick.row}
                status={statusPick.status}
                onClose={() => setStatusPick(null)}
                onSubmit={(comments) => {
                  setStatusM.mutate({ id: statusPick.row.id, status: statusPick.status, comments });
                  setStatusPick(null);
                  setEditing(null);
                }}
              />
            ) : (
              <EditProfileModal
                proposal={editing}
                mode="ae"
                variant="side"
                title={`Quality Check — ${editing.firstName} ${editing.lastName}`}
                canEdit={can("qcConfirmDoubleCheck", qcAuth)}
                statusOptions={SETTABLE_STATUSES.filter(
                  (t) =>
                    new Set(nextStates("reviewer", editing.status)).has(t) &&
                    (can(STATUS_ACTIONS[t]!, qcAuth) || (t === "Invite" && can("qcJuniorInvite", qcAuth))),
                )}
                onPickStatus={(status) => setStatusPick({ row: editing, status })}
                onClose={() => setEditing(null)}
              />
            )}
            {editing.selectedId ? (
              <aside className="hidden w-[22rem] shrink-0 overflow-y-auto border-l border-slate-200 bg-slate-50/70 p-4 md:block">
                <h4 className="mb-2 text-xs font-semibold text-slate-600">
                  Reviewer profile — who we're looking for
                </h4>
                <ReviewerProfilePanel
                  selectedId={editing.selectedId}
                  canEdit={can("editSimilarArticles", qcAuth) || can("editPitchDeck", qcAuth)}
                />
              </aside>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

/** WO-7/22 Track G/J: the one-button status flow — pick a status, add the (required or optional)
 *  comment, and a single action sets both. Replaces the cross-browser-broken prompt popup. */
function StatusCommentModal({
  row,
  status,
  onClose,
  onSubmit,
}: {
  row: QcRow;
  status: ReviewerStatus;
  onClose: () => void;
  onSubmit: (comments?: string) => void;
}): JSX.Element {
  const [comment, setComment] = useState("");
  const prompt = COMMENT_PROMPTS[status];
  const optional = OPTIONAL_COMMENT_STATUSES.has(status);
  const blocked = !!prompt && !optional && !comment.trim();
  const heading = `Set ${status} — ${row.firstName} ${row.lastName}`;
  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold">{heading}</h2>
        <Button variant="ghost" onClick={onClose} aria-label="Close">
          ✕
        </Button>
      </div>
      <div className="flex-1 overflow-auto px-4 py-4">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          On “{row.preprintTitle}” — currently <Badge tone={statusTone(row.status)}>{row.status}</Badge>
        </p>
        {prompt ? (
          <Field label={prompt}>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} autoFocus />
          </Field>
        ) : (
          <p className="text-sm text-slate-600">No comment needed for this status.</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(comment.trim() ? comment : undefined)} disabled={blocked}>
            Set {status}{prompt ? " & save comment" : ""}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}

function QcProposalRow({
  p,
  canConfirmDouble,
  canForStatus,
  claimed,
  mine,
  onToggleClaim,
  canEditProfile,
  onQualityCheck,
  claimPending = false,
}: {
  p: QcRow;
  canConfirmDouble: boolean;
  canForStatus: (t: ReviewerStatus) => boolean;
  claimed: boolean;
  mine: boolean;
  onToggleClaim: () => void;
  canEditProfile: boolean;
  /** Opens the Quality Check modal (editable details + Set status). */
  onQualityCheck: () => void;
  claimPending?: boolean;
}): JSX.Element {
  const valid = new Set(nextStates("reviewer", p.status));
  const options = SETTABLE_STATUSES.filter((t) => valid.has(t) && canForStatus(t));
  const canOpenQc = canEditProfile || options.length > 0;
  const pubmedHrefExact = extHref(p.linkToPublications ?? p.pubMedLink);
  const profileHref = extHref(p.linkToProfile);
  const bmpHref = p.doiBestMatch?.trim() ? bestMatchHref(p.doiBestMatch) : null;
  const qcCheckedBy = qcCheckedByLine(p);
  const claimerLabel = p.qcClaimedByName?.trim() || p.qcClaimedByEmail;
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 rounded-md border p-2 ${claimed ? "border-brand bg-brand/5" : "border-slate-100"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
          {p.hIndex != null && <Badge tone="violet">H {p.hIndex}</Badge>}
          {p.team && <Badge tone="slate">{p.team}</Badge>}
        </div>
        {/* Frida-QC R-013/R-014: Double-check is the junior→senior assist path — credentials are
            already verified; set status → Invite credits the junior and does NOT take over. */}
        {p.status === "Double Check" && (
          <div className="mt-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-800">
            <span className="font-semibold">Double check — senior assist requested{p.qcTrainingEmail ? ` by ${p.qcTrainingEmail}` : ""}.</span>{" "}
            {canConfirmDouble
              ? "Credentials are already verified — review the expertise + best-match, then Quality Check → Set status → Invite. The requesting QC keeps credit; assisting here doesn't take over their preprint."
              : "Awaiting a senior QC's confirmation."}
          </div>
        )}
        {/* FR-K12: the full reviewer card — the five things + the proposing student. */}
        <h3 className="mt-1 font-medium">
          {p.firstName} {p.lastName}
          {p.reviewerEmail && <span className="ml-2 text-xs font-normal text-slate-500">{p.reviewerEmail}</span>}
        </h3>
        <p className="text-xs text-slate-500">{p.affiliation?.trim() || "—"}</p>
        {claimed && claimerLabel ? (
          <p className="text-xs font-semibold text-brand">Claimed by {claimerLabel}</p>
        ) : null}
        <div className="mt-0.5 flex flex-wrap gap-3 text-xs">
          {pubmedHrefExact && (
            <ExtLink href={pubmedHrefExact} title={PUBMED_TIP}>
              PubMed
            </ExtLink>
          )}
          {profileHref && <ExtLink href={profileHref}>Institutional profile</ExtLink>}
          {bmpHref && <ExtLink href={bmpHref}>Best-match paper</ExtLink>}
        </div>
        {p.justification && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">
            <span className="font-medium not-italic text-slate-500">Justification:</span>{" "}
            <span className="italic">{p.justification}</span>
          </p>
        )}
        {/* WO-7/22 Track F [28:39–29:10]: the student's COI pushback, surfaced to the AE. */}
        {p.coiContestJustification && (
          <p className="mt-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-800">
            <span className="font-semibold">COI contested by the student</span> — PROFI flagged a conflict
            {p.coiSummary ? ` (${p.coiSummary})` : ""}; they pushed back:{" "}
            <span className="italic">{p.coiContestJustification}</span>
          </p>
        )}
        <p className="mt-1 text-xs text-slate-500">
          <span className="font-medium">Proposed by:</span> {p.studentName || "—"}
          {p.proposerEmail && <span className="ml-1">({p.proposerEmail})</span>}
          {p.dateOfProposal && <span className="ml-2 text-slate-400">on {p.dateOfProposal.slice(0, 10)}</span>}
        </p>
        {qcCheckedBy && (
          <p className="mt-1 text-xs text-slate-500">
            <span className="font-medium">Quality Checked by:</span> {qcCheckedBy}
          </p>
        )}
        {p.qcComments && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">QC (student-facing): {unescapeNewlines(p.qcComments)}</p>
        )}
        {p.qcInternalNote && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-violet-700">Internal note (AE↔AE): {unescapeNewlines(p.qcInternalNote)}</p>
        )}
        {p.studentReply && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Student reply: {unescapeNewlines(p.studentReply)}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Frida-QC R-008: claim pins the row and shows the AE name to everyone in the queue. */}
        <Button
          variant="ghost"
          className="py-1"
          onClick={onToggleClaim}
          disabled={claimPending}
          aria-pressed={mine}
          title={
            mine
              ? "Release — stop claiming this row"
              : claimed
                ? `Take over from ${claimerLabel}`
                : "Claim — show other AEs that you're working this row"
          }
        >
          {mine ? "Release" : "📌 Claim"}
        </Button>
        {/* Single entry: editable details + Set status live inside the Quality Check modal. */}
        {canOpenQc && (
          <Button
            variant="secondary"
            className="py-1"
            onClick={onQualityCheck}
            disabled={!mine}
            title={mine ? undefined : "Claim this row first"}
          >
            Quality Check
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Student "My Quality Checks" (FR-J14, July-7 FR-K16, WO-7/22 Track H) ─────
// Mirrors the AE portal [46:52–48:52]: EVERY status groups by preprint — the ideal-reviewer
// description sits on one side and the student's proposals + statuses on the other — with
// status/domain filters [48:52–49:38]. READ-ONLY apart from the Fixable → Edit → Fixed loop.
// Home's Needs-Attention CTA lands here with ?attention=1 — Fixable rows lead and scroll into view.
function MyQualityChecks(): JSX.Element {
  const [searchParams] = useSearchParams();
  const focusAttention = searchParams.get("attention") === "1";
  const [editing, setEditing] = useState<QcRow | null>(null);
  // WO-7/23 Track F [30:21–33:10]: the student page mirrors the AE portal — same queue tabs, same
  // filter row. The status filter opens PRE-FILTERED to Fixable: that is what a student comes here
  // to do. "All statuses" is one click away for the rest.
  const [statusFilter, setStatusFilter] = useState<ReviewerStatus | "">("Fixable");
  const [domainFilter, setDomainFilter] = useState<Domain | "">("");
  const [teamsSel, setTeamsSel] = useState<string[]>([]);
  const [queue, setQueue] = useState<QueueKind>("in-process");
  const scrolledAttention = useRef(false);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ mine: "1" }),
    queryFn: () => api.get<QcRow[]>(`/quality-check/mine`),
  });

  const rows = data ?? [];
  const presentStatuses = useMemo(() => [...new Set(rows.map((p) => p.status))].sort(), [rows]);
  const filtered = useMemo(
    () =>
      rows.filter((p) => {
        if (statusFilter && p.status !== statusFilter) return false;
        if (domainFilter && p.domain !== domainFilter) return false;
        if (teamsSel.length && !(p.team && teamsSel.includes(p.team))) return false;
        // Same in-process / complete split as the AE queue (Frida-QC R-003/R-004).
        if (queue !== "all") {
          const done = p.preprintStatus != null && COMPLETE_QUEUE_STATUSES.has(p.preprintStatus);
          if (queue === "complete" ? !done : done) return false;
        }
        return true;
      }),
    [rows, statusFilter, domainFilter, teamsSel, queue],
  );
  const needsFix = useMemo(() => filtered.filter((p) => p.status === "Fixable"), [filtered]);

  // Track H: one grouped list for ALL statuses; groups that need a fix lead, and rows inside a
  // group keep the attention order (Fixable → COI Detected → Reject → rest).
  const groups = useMemo(() => {
    const byPreprint = new Map<
      string,
      { title: string; selectedId: string | null; abstract: string | null; rows: QcRow[] }
    >();
    for (const p of filtered) {
      const key = p.preprintId ?? "unknown";
      const g = byPreprint.get(key) ?? {
        title: p.preprintTitle,
        selectedId: p.selectedId,
        abstract: p.preprintAbstract,
        rows: [],
      };
      if (!g.selectedId && p.selectedId) g.selectedId = p.selectedId;
      if (!g.abstract && p.preprintAbstract) g.abstract = p.preprintAbstract;
      g.rows.push(p);
      byPreprint.set(key, g);
    }
    const hasFix = (g: { rows: QcRow[] }): boolean => g.rows.some((r) => r.status === "Fixable");
    const arr = [...byPreprint.entries()];
    for (const [, g] of arr) {
      g.rows.sort((a, b) => reviewerAttentionRank(a.status) - reviewerAttentionRank(b.status));
    }
    arr.sort((a, b) => Number(hasFix(b[1])) - Number(hasFix(a[1])));
    return arr;
  }, [filtered]);

  // Deep-link from Home: scroll to the first Fixable row (do not auto-open Edit).
  useEffect(() => {
    if (!focusAttention || scrolledAttention.current || needsFix.length === 0) return;
    scrolledAttention.current = true;
    document.getElementById(`qc-row-${needsFix[0]!.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusAttention, needsFix]);

  return (
    <div className="space-y-4">
      <header className={QC_STICKY_BAR}>
        <div>
          <h1 className="text-xl font-bold">My Quality Checks</h1>
          <p className="text-sm text-slate-500">
            Your proposals the quality checkers have acted on, grouped by preprint (still-unchecked ones stay in
            Find Reviewers). When a reviewer is marked Fixable, open Edit to update the profile and mark it Fixed.
          </p>
        </div>
        {/* Track H/F filters — view/filter only; students don't quality-check [49:11–49:38].
            WO-7/23 Track F: same control set as the AE portal (queue tabs, domain, country/team,
            status), so the two pages read as one product. */}
        <div className="flex flex-wrap items-center gap-2">
          <QueueTabs queue={queue} onChange={setQueue} />
          <Select
            value={domainFilter}
            aria-label="Filter by domain"
            onChange={(e) => setDomainFilter(e.target.value as Domain | "")}
          >
            <option value="">All domains</option>
            {DOMAINS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
          <MultiFilter label="Country / Team" options={RRID_COUNTRY_TEAMS} value={teamsSel} onChange={setTeamsSel} />
          <Select
            value={statusFilter}
            aria-label="Filter by status"
            onChange={(e) => setStatusFilter(e.target.value as ReviewerStatus | "")}
          >
            <option value="">All statuses</option>
            {presentStatuses.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </div>
      </header>
      {isLoading ? (
        <Spinner label="Loading your proposals…" />
      ) : error ? (
        <EmptyState>Could not load your proposals.</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState>You haven’t proposed any reviewers yet.</EmptyState>
      ) : groups.length === 0 ? (
        <EmptyState>
          {statusFilter === "Fixable" ? (
            <>
              Nothing needs a fix right now. 🎉{" "}
              <button type="button" className="font-medium text-brand hover:underline" onClick={() => setStatusFilter("")}>
                Show all statuses
              </button>
            </>
          ) : (
            "No proposals match these filters."
          )}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {groups.map(([preprintId, g]) => {
            const fixes = g.rows.filter((r) => r.status === "Fixable").length;
            return (
              <div
                key={preprintId}
                className={`rounded-lg border bg-white px-4 py-3 shadow-sm ${fixes > 0 ? "border-amber-300" : "border-slate-200"}`}
              >
                <PreprintContext
                  selectedId={g.selectedId}
                  title={g.title}
                  abstract={g.abstract}
                  trailing={
                    fixes > 0 ? (
                      <span className="text-xs font-semibold text-amber-700">
                        {fixes} need your fix
                      </span>
                    ) : undefined
                  }
                  proposedCount={g.rows.length}
                  proposedDefaultOpen={fixes > 0}
                  proposedReviewers={
                    /* Track H: ideal-reviewer description on one side, the proposals on the other. */
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
                      <div className="space-y-2">
                        {g.rows.map((p) => (
                          <StudentQcCard
                            key={p.id}
                            proposal={p}
                            highlight={focusAttention && p.id === needsFix[0]?.id}
                            onEdit={() => setEditing(p)}
                          />
                        ))}
                      </div>
                      {g.selectedId && (
                        <aside className="h-fit rounded-md border border-slate-100 bg-slate-50/60 p-3 lg:sticky lg:top-16">
                          <h4 className="mb-2 text-xs font-semibold text-slate-600">
                            Ideal reviewer — what the AE is looking for
                          </h4>
                          <ReviewerProfilePanel selectedId={g.selectedId} />
                        </aside>
                      )}
                    </div>
                  }
                />
              </div>
            );
          })}
        </div>
      )}
      {editing && <EditProfileModal proposal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function StudentQcCard({
  proposal: p,
  highlight = false,
  onEdit,
}: {
  proposal: QcRow;
  highlight?: boolean;
  onEdit: () => void;
}): JSX.Element {
  const pubmedHrefExact = extHref(p.linkToPublications ?? p.pubMedLink);
  const profileHref = extHref(p.linkToProfile);
  const bmpHref = p.doiBestMatch?.trim() ? bestMatchHref(p.doiBestMatch) : null;
  const qcCheckedBy = qcCheckedByLine(p);
  return (
    <div id={`qc-row-${p.id}`}>
      <Card className={highlight ? "ring-2 ring-amber-400" : undefined}>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge tone={statusTone(p.status)}>{p.status}</Badge>
              {p.domain && <Badge tone="blue">{p.domain}</Badge>}
            </div>
            <h3 className="mt-1 font-medium">
              {p.firstName} {p.lastName}
            </h3>
            <p className="text-xs text-slate-500">{p.affiliation?.trim() || "—"}</p>
            {(pubmedHrefExact || profileHref || bmpHref) && (
              <p className="flex flex-wrap gap-3 text-xs">
                {profileHref && <ExtLink href={profileHref}>Institutional profile</ExtLink>}
                {pubmedHrefExact && (
                  <ExtLink href={pubmedHrefExact} title={PUBMED_TIP}>
                    PubMed
                  </ExtLink>
                )}
                {bmpHref && <ExtLink href={bmpHref}>Best-match paper</ExtLink>}
              </p>
            )}
            {qcCheckedBy && (
              <p className="mt-1 text-xs text-slate-500">
                <span className="font-medium">Quality Checked by:</span> {qcCheckedBy}
              </p>
            )}
            {p.qcComments && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">
                AE note: {unescapeNewlines(p.qcComments)}
              </p>
            )}
            {p.studentReply && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Your reply: {unescapeNewlines(p.studentReply)}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Edit opens the profile modal; Mark as Fixed is only inside that modal. */}
            {p.status === "Fixable" && (
              <Button variant="secondary" onClick={onEdit}>
                Edit
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** Reviewer profile editor.
 *  - Student (default): PATCH profile + optional reply; Save Changes and/or Mark as Fixed (FR-K16).
 *  - AE (`mode="ae"`): same editable fields (senior-gated) with Set status at the bottom instead of
 *    Mark as Fixed — the single Quality Check entry from the AE queue. */
function EditProfileModal({
  proposal,
  onClose,
  title,
  mode = "student",
  variant = "modal",
  canEdit = true,
  statusOptions = [],
  onPickStatus,
}: {
  proposal: QcRow;
  onClose: () => void;
  title?: string;
  mode?: "student" | "ae";
  /** AE Quality Check sits on the left so the reviewer profile stays visible. */
  variant?: "modal" | "side";
  /** AE: false for juniors who can still Set status but not PATCH the profile. */
  canEdit?: boolean;
  statusOptions?: ReviewerStatus[];
  onPickStatus?: (status: ReviewerStatus) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const ae = mode === "ae";
  const [f, setF] = useState({
    affiliation: proposal.affiliation ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    otherDegree: proposal.otherDegree ?? "",
    title: proposal.title ?? "",
    otherTitles: proposal.otherTitles ?? "",
    subdiscipline: proposal.subdiscipline ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    profileJustification: proposal.profileJustification ?? "",
    // Legacy SoftR parity (Angel, 8/20): PubMed defaults to a first+last name search, editable.
    publicationLink:
      proposal.linkToPublications?.trim() ||
      proposal.pubMedLink?.trim() ||
      pubmedSearchUrl(proposal.firstName, proposal.lastName),
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
    bestMatchUrl: proposal.doiBestMatch ?? "",
  });
  const [reply, setReply] = useState("");
  const [saved, setSaved] = useState(false);
  /**
   * WO-7/23 Track F [37:26–38:20]: TWO distinct student actions.
   *  - "Save Changes" persists the profile (+ reply) and leaves the item in the queue;
   *  - "Mark as Fixed" does the same write AND clears it.
   * AE mode replaces Mark as Fixed with Set status (status+comment modal).
   */
  const persist = async (): Promise<void> => {
    await api.patch(`/quality-check/${proposal.id}/profile`, f);
    if (!ae && reply.trim()) await api.post(`/quality-check/${proposal.id}/reply`, { comment: reply.trim() });
  };
  const saveOnly = useMutation({
    mutationFn: persist,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qc"] });
      setReply("");
      setSaved(true);
    },
  });
  const saveAndFix = useMutation({
    mutationFn: async () => {
      await persist();
      await api.post(`/quality-check/${proposal.id}/fixed`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const set = (key: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF({ ...f, [key]: e.target.value });
  const incomplete =
    !f.highestDegree.trim() ||
    !f.title.trim() ||
    !f.linkToProfile.trim() ||
    !f.profileJustification.trim() ||
    !f.publicationLink.trim();
  const readOnly = ae && !canEdit;
  const heading =
    title ??
    (ae
      ? `Quality Check — ${proposal.firstName} ${proposal.lastName}`
      : `Edit reviewer profile — ${proposal.firstName} ${proposal.lastName}`);
  const form = (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>On “{proposal.preprintTitle}”</span>
          <Badge tone={statusTone(proposal.status)}>{proposal.status}</Badge>
        </div>
        {!ae && proposal.qcComments && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            <span className="font-semibold">The AE asked:</span> {unescapeNewlines(proposal.qcComments)}
          </div>
        )}
        {ae && proposal.qcComments && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            <span className="font-semibold">QC note (student-facing):</span> {unescapeNewlines(proposal.qcComments)}
          </div>
        )}
        <Field label="Affiliation">
          <Input value={f.affiliation} onChange={set("affiliation")} disabled={readOnly} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Secondary affiliation">
            <Input value={f.secondaryAffiliation} onChange={set("secondaryAffiliation")} disabled={readOnly} />
          </Field>
          <HighestDegreeField
            value={f.highestDegree}
            onChange={(v) => setF({ ...f, highestDegree: v })}
            required
            disabled={readOnly}
          />
        </div>
        <Field label="Other Degrees">
          <Input value={f.otherDegree} onChange={set("otherDegree")} disabled={readOnly} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Title" required>
            <Input value={f.title} onChange={set("title")} required disabled={readOnly} />
          </Field>
          <Field label="Other titles">
            <Input value={f.otherTitles} onChange={set("otherTitles")} disabled={readOnly} />
          </Field>
        </div>
        <ClickableUrlField
          label="Institutional profile (link)"
          value={f.linkToProfile}
          onChange={(v) => setF({ ...f, linkToProfile: v })}
          required
          disabled={readOnly}
        />
        <Field label="Justification if not institutional profile" required>
          <p className="mb-1 text-xs text-slate-500">
            If you provided the Institutional Profile (link) above, please write N/A in the field.
          </p>
          <Textarea
            value={f.profileJustification}
            onChange={set("profileJustification")}
            rows={3}
            required
            disabled={readOnly}
          />
        </Field>
        <ClickableUrlField
          label="Publication / PubMed link"
          value={f.publicationLink}
          onChange={(v) => setF({ ...f, publicationLink: v })}
          placeholder="https://pubmed.ncbi.nlm.nih.gov/…"
          required
          disabled={readOnly}
        />
        <Field label="Because of your expertise in…">
          <Input
            value={f.subdiscipline}
            onChange={set("subdiscipline")}
            placeholder="e.g. computational epidemiology"
            disabled={readOnly}
          />
        </Field>
        <Field label="Best-match paper title">
          {f.bestMatchUrl.trim() && f.bestMatchPaperTitle.trim() ? (
            <div className="space-y-1.5">
              <a
                className="block text-sm text-brand hover:underline"
                href={bestMatchHref(f.bestMatchUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {f.bestMatchPaperTitle}
              </a>
              <Input
                value={f.bestMatchPaperTitle}
                onChange={set("bestMatchPaperTitle")}
                disabled={readOnly}
                aria-label="Edit best-match paper title"
              />
            </div>
          ) : (
            <Input value={f.bestMatchPaperTitle} onChange={set("bestMatchPaperTitle")} disabled={readOnly} />
          )}
        </Field>
        <ClickableUrlField
          label="Best-match paper URL"
          value={f.bestMatchUrl}
          onChange={(v) => setF({ ...f, bestMatchUrl: v })}
          hrefFor={bestMatchHref}
          placeholder="https://doi.org/10.…"
          disabled={readOnly}
        />
        <div className="rounded-md border border-blue-100 bg-blue-50 p-2.5 text-sm text-blue-950">
          <span className="text-xs font-semibold text-blue-900">Justification preview</span>
          <p className="mt-1 text-xs text-blue-800/80">
            This sentence is copied into the reviewer invitation email.
          </p>
          <p className="mt-1.5 italic">
            Because of your expertise in {f.subdiscipline.trim() || "…"}, and especially your paper "
            {f.bestMatchPaperTitle.trim() || "…"}", we think you'd be a terrific reviewer for this preprint.
          </p>
        </div>
        {!ae && (
          <Field label="Reply to the checker">
            <Input
              placeholder="e.g. “I’ve made the correction”"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
          </Field>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
          {saved && !saveOnly.isPending && (
            <span className="mr-auto text-xs font-medium text-green-700" role="status">
              {ae ? "✓ Saved" : "✓ Saved — still on your list until you mark it fixed."}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canEdit && (
            <Button
              variant="secondary"
              onClick={() => saveOnly.mutate()}
              disabled={saveOnly.isPending || saveAndFix.isPending}
            >
              {saveOnly.isPending ? "Saving…" : "Save Changes"}
            </Button>
          )}
          {!ae && (
            <Button onClick={() => saveAndFix.mutate()} disabled={incomplete || saveAndFix.isPending || saveOnly.isPending}>
              {saveAndFix.isPending ? "Marking…" : "Mark as Fixed"}
            </Button>
          )}
          {ae && statusOptions.length > 0 && onPickStatus && (
            <Select
              value=""
              aria-label="Set status"
              className="min-w-[10rem]"
              onChange={(e) => {
                const t = e.target.value as ReviewerStatus;
                e.target.value = "";
                if (!t) return;
                onPickStatus(t);
              }}
            >
              <option value="">Set status…</option>
              {statusOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>
  );
  if (variant === "side") {
    return (
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={heading}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold">{heading}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-4">{form}</div>
      </div>
    );
  }
  return (
    <Modal open onClose={onClose} title={heading}>
      {form}
    </Modal>
  );
}
